import { isObject } from 'lodash-es';
import { useContext, useEffect, type FC } from 'react';

import { GlobalMessagesContext } from '../GlobalMessages';

import { getErrorMessage, type AppError } from './errorMessage';

interface HandledError extends Error {
  handled?: boolean;
}

const isHandledError = (error: AppError): error is HandledError => isObject(error);

export const ErrorCatcher: FC = () => {
  const { triggerMessage } = useContext(GlobalMessagesContext);

  useEffect(() => {
    const errorEventListener = (event: ErrorEvent): boolean | undefined => {
      const error: AppError = event.error as AppError;

      if (isHandledError(error) && Boolean(error.handled)) {
        return true;
      }

      triggerMessage({
        title: 'Application Error',
        body: `An unhandled error "${getErrorMessage(error)}" occurred.`,
      });

      if (isHandledError(error)) {
        error.handled = true;
      }

      return;
    };

    window.addEventListener('error', errorEventListener); // eslint-disable-line @typescript-eslint/strict-void-return

    return () => {
      window.removeEventListener('error', errorEventListener); // eslint-disable-line @typescript-eslint/strict-void-return
    };
  }, [triggerMessage]);

  return null;
};
